import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import { formatCurrency } from "../../utils/formatters";
import { ExternalLink, Copy, Check } from 'lucide-react';
import { useParams } from "react-router";
import api from "../../services/api";
import authService from "../../services/authService";
import Pagination from "../ui/pagination/Pagination";
import ComponentCard from "../common/ComponentCard";

interface AffiliateOfferLink {
    id: string;
    affiliateOfferLinkId: string;
    affiliateCompanyId: string;
    offerId: string;
    paymentLinkId: string;
    code: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    settingsData: string | null;
    offer: {
        id: string;
        name: string;
        price: string;
    };
    paymentLink: {
        id: string;
        name: string | null;
    };
}

interface ProductOfferTableProps {
    productId?: string;
}

export default function ProductOfferTable({ productId }: ProductOfferTableProps) {
    const { productId: paramId } = useParams<{ productId: string }>();
    const productParticipantsId = productId || paramId;
    const [offers, setOffers] = useState<AffiliateOfferLink[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        const fetchAffiliateOfferLinks = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await api.get(`/api/product-participants/affiliate-links/${productParticipantsId}`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.data) {
                    setOffers(response.data.data); // Ajuste para acessar o array de links
                } else {
                    setOffers([]);
                }
            } catch (err: any) {
                console.error("Erro ao buscar links de afiliado:", err);
                setError(err.message || "Erro ao carregar links de afiliado");
            } finally {
                setLoading(false);
            }
        };

        if (productParticipantsId) {
            fetchAffiliateOfferLinks();
        }
    }, [productParticipantsId]);

    // Função para montar o link de venda
    const generateSalesLink = (offer: AffiliateOfferLink) => {
        return `https://pay.sellyo.com.br/p/${offer.code}`;
    };

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = offers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(offers.length / itemsPerPage);

    // Componente para exibir e copiar o link
    const CopyLinkInput = ({ url }: { url: string }) => {
        const [copied, setCopied] = useState(false);

        const handleCopy = () => {
            navigator.clipboard.writeText(url)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                })
                .catch(err => {
                    console.error('Erro ao copiar: ', err);
                });
        };

        return (
            <div className="relative flex items-center w-full">
                <input
                    type="text"
                    value={url}
                    readOnly
                    onClick={handleCopy}
                    className="w-full py-2 px-3 pr-12 border border-gray-300 rounded text-sm text-gray-700 bg-gray-50 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                />
                <div
                    onClick={handleCopy}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-800/40 rounded-md cursor-pointer transition-colors"
                    title="Copiar link"
                >
                    {copied ? <Check size={18} className="text-green-600 dark:text-green-400" /> : <Copy size={18} className="text-blue-600 dark:text-blue-400" />}
                </div>
            </div>
        );
    };

    if (loading) {
        return <div className="p-4 text-center">Carregando links...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-red-500">Erro: {error}</div>;
    }

    if (offers.length === 0) {
        return (
            <ComponentCard title="Ofertas">
                <div className="p-8 text-center text-gray-500">
                    Nenhum link encontrado.
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Todos os Links">
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Campo de busca - com largura limitada */}
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar link..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl">
                <div className="max-w-full overflow-x-auto">
                    <Table>
                        {/* Table Header */}
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Nome
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Preço
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Link
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Acessar
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        {/* Table Body */}
                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {currentItems.map((offer) => (
                                <TableRow key={offer.id}>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {offer.offer.name}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {formatCurrency(parseFloat(offer.offer.price))}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div className="flex flex-col gap-1">
                                            <CopyLinkInput url={generateSalesLink(offer)} />
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={offer.status === "active" ? "success" : "error"}
                                        >
                                            {offer.status === "active" ? "Ativo" : "Inativo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <a
                                            href={generateSalesLink(offer)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1 text-blue-500 hover:text-blue-700"
                                        >
                                            Visualizar <ExternalLink size={14} />
                                        </a>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        </ComponentCard>
    );
}